import {Component, OnInit} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {SchedulePreviewStubFactory} from '@application/helper/calendar/schedule/schedule-preview-stub-factory';
import {PatternShift} from '@domain/calendar/pattern/pattern-shift';
import {SchedulePreview} from '@domain/calendar/schedule/schedule-preview';
import {CalendarTimelineService} from '@presentation/pages/calendar/calendar-timeline.service';
import {
  BaseComponent,
  CellButtonComponent,
  CellButtonParams,
  ColDefBuilderFactoryService,
  CrudOverviewButtonConfig,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DateTimeFormat,
  DateTimePipe,
  EmptyCellRendererComponent,
  GridOptionsBuilder,
  TranslateService
} from '@vdw/angular-component-library';
import {CellRendererSelectorResult, ColDef, GetRowIdParams, ICellRendererParams, ITooltipParams, RowClassParams, RowDataUpdatedEvent, ValueGetterParams} from 'ag-grid-community';
import {DateTime, Duration} from 'luxon';
import {SchedulePreviewRowData} from './schedule-preview-row-data.interface';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html'
})
export class ScheduleComponent extends BaseComponent implements OnInit {
  public config: CrudOverviewDataConfig<SchedulePreviewRowData>;
  private readonly TODAY = DateTime.now().startOf('day');
  private readonly DATE_COLUMN_WIDTH = 90;
  private readonly CELL_BUTTON_COLUMN_WIDTH = 32;

  public constructor(
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<SchedulePreviewRowData>,
    private readonly calendarTimelineService: CalendarTimelineService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly dateTimePicker: DateTimePipe
  ) {
    super();
  }

  public ngOnInit(): void {
    this.config = this.builderFactoryService
      .getBuilder()
      .withGridOptions(this.getColumnDefs(), GridIdentifier.SCHEDULE, this.getSchedulePreviewRowData(), (builder: GridOptionsBuilder) => {
        builder
          .withGetRowId((params: GetRowIdParams) => params.data.date)
          .withoutRowSelection()
          .withOnRowDataUpdated((event: RowDataUpdatedEvent) => event.api.refreshHeader())
          .withRowClassRules({
            ['today-marker']: (params: RowClassParams) => params.data.date.hasSame(this.TODAY, 'day'),
            ['past-day']: (params: RowClassParams) => params.data.date < this.TODAY,
            ['weekend']: (params: RowClassParams) => [6, 7].includes(params.data.date.weekday),
            ['non-working-day']: (params: RowClassParams) => !params.data.isWorkingDay
          });
        return builder.build();
      })
      .build();
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('date')
        .withValueGetter((params: ValueGetterParams) => this.dateTimePicker.transform(params.data.date, DateTimeFormat.DATE_SHORT))
        .withTooltipValueGetter((params: ITooltipParams) => (!params.data.isWorkingDay ? this.translate.instant('ANGULAR_COMPONENT_LIBRARY.GENERAL.NON_WORKING_DAY') : null))
        .withMinWidth(this.DATE_COLUMN_WIDTH)
        .withMaxWidth(this.DATE_COLUMN_WIDTH)
        .withResizable(false)
        .withoutFilter()
        .withSuppressMovable('left')
        .withSuppressHeaderMenuButton()
        .build(),
      this.calendarTimelineService.getColumnDef(this.colDefBuilderFactoryService, this.unSubscribeOnViewDestroy),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('isWorkingDay')
        .withHeaderName('')
        .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
          if (params.data.date < this.TODAY.plus({days: 1})) {
            return {component: EmptyCellRendererComponent};
          }
          return {
            component: CellButtonComponent,
            params: {
              buttons: [
                {
                  icon: params?.value ? 'solid-do-not-disturb-on' : 'solid-do-not-disturb-off',
                  title: params?.value ? 'ANGULAR_COMPONENT_LIBRARY.GENERAL.MARK_AS_NON_WORKING_DAY' : 'ANGULAR_COMPONENT_LIBRARY.GENERAL.MARK_AS_WORKING_DAY',
                  onClick: (): void => {
                    params.node.setDataValue('isWorkingDay', !params.value);
                    params.node.setDataValue('scheduleItems', []);
                  }
                } as CrudOverviewButtonConfig
              ]
            } as CellButtonParams
          };
        })
        .withMaxWidth(this.CELL_BUTTON_COLUMN_WIDTH)
        .withResizable(false)
        .withoutFilter()
        .withSuppressMovable('right')
        .withSuppressHeaderMenuButton()
        .build()
    ];
  }

  private getSchedulePreviewRowData(): SchedulePreviewRowData[] {
    const rowData = SchedulePreviewStubFactory.getRandomSchedulePreview().map(
      (data: SchedulePreview) =>
        ({
          date: data.date,
          isWorkingDay: data.isWorkingDay,
          scheduleItems: data.shifts.map((shift: PatternShift) => this.calendarTimelineService.getScheduleItemFromShift(shift))
        }) as SchedulePreviewRowData
    );

    this.calendarTimelineService.startTime = rowData.reduce((minStartTime: Duration, {scheduleItems}: SchedulePreviewRowData) => {
      if (scheduleItems.length > 0) {
        const firstStartTime = scheduleItems[0].startTime;
        if (minStartTime === null) {
          return firstStartTime;
        }
        return firstStartTime < minStartTime ? firstStartTime : minStartTime;
      }
      return minStartTime;
    }, null);
    this.calendarTimelineService.updateTotalHours(rowData);

    return rowData;
  }
}
